/**
 * Shop Suggestion
 *
 * Filename: shop_suggestion.js
 *
 * @category  Fahrradxxl
 * @author    Shervin Mirostovar <s.mirostovar@fahrrad-xxl.de>
 *            Janik Kemnade      <j.kemnade@fahrrad-xxl.de>
 * @copyright 2024 Fahrrad-xxl.de
 */



/* 📜 INFORMATION
 * --------------------------------------------------------------------------------------------------------------------
 *
 *
 */



/* 📑 TODO
 * --------------------------------------------------------------------------------------------------------------------
 *
 */






/* 📦️ IMPORTS
 * ----------------------------------------------------------------------------------------------------------------- */
import FxxlCore from '@fxxl/core';
import FxxlError from '@fxxl/error';
import {
    langcode,
    presence_id,
} from '@fxxl/globals';






/* ✒️ CLASS: FxxlShopSuggestion
 * ----------------------------------------------------------------------------------------------------------------- */
export default class FxxlShopSuggestion {

    /* 🐙 STATIC Helper -> Push ( Event, Data )
     * ----------------------------------------------------------------------------------------- */
    /**
     * Erzeugt aus den unterstützten Ländercodes eine stabile Storage-Version.
     * Ändert sich die Länderliste, werden bestehende Einträge einmalig neu geprüft.
     *
     * @returns {string}
     */
    static getShopSuggestionCountryVersion() {
        const countries = FxxlCore.getConfig()?.server?.shop_suggestion_countries ?? [];
        if (!Array.isArray(countries)) {
            return '';
        }

        const countryCodes = countries
            .map((country) => country?.iso_code_2)
            .filter((countryCode) => typeof countryCode === 'string' && countryCode.trim() !== '')
            .map((countryCode) => countryCode.trim().toLowerCase());

        return countryCodes
            .filter((countryCode, index) => countryCodes.indexOf(countryCode) === index)
            .sort()
            .join('-');
    }

    static setStorageSelectedSuggestedCountry(selectedCountryISO) {
        const storageKey     = FxxlCore.getConfig()?.storageKeys?.shopSuggestion;
        const data           = FxxlCore.getStorage(storageKey) || {};
        const countryVersion = FxxlShopSuggestion.getShopSuggestionCountryVersion();

        if (!selectedCountryISO) {
            const defaultPresenceLanguageCode = FxxlCore.getConfig()?.server?.presence?.default_language_code || langcode;
            const configuredCountries = FxxlCore.getConfig()?.server?.shop_suggestion_countries;
            const countries      = Array.isArray(configuredCountries) ? configuredCountries : [];
            const currentCountry = countries.find((country) => +country?.c_presence_id === +presence_id);
            selectedCountryISO   = currentCountry?.iso_code_2 ?? defaultPresenceLanguageCode;
        }

        if (selectedCountryISO) {
            data.selectedCountryISO = selectedCountryISO.toLowerCase();
            data.countryChecked     = true;
            data.countryVersion     = countryVersion;
            FxxlCore.setStorage(storageKey, data);
        }
    }

    static isSetStorageSelectedSuggestedCountry() {
        const storageKeyShopSuggestion  = FxxlCore.getConfig()?.storageKeys?.shopSuggestion;
        const storageDataShopSuggestion = FxxlCore.getStorage(storageKeyShopSuggestion);
        const selectedCountry           = storageDataShopSuggestion?.selectedCountryISO;
        const countryVersion            = FxxlShopSuggestion.getShopSuggestionCountryVersion();

        return storageDataShopSuggestion?.countryChecked === true
            && storageDataShopSuggestion?.countryVersion === countryVersion
            && typeof selectedCountry === 'string' && selectedCountry !== '';
    }




    /* 🏷️ VARS
     * ----------------------------------------------------------------------------------------- */
    get countries() {
        const countries = FxxlCore.getConfig()?.server?.shop_suggestion_countries;
        return Array.isArray(countries) ? countries : [];
    }

    get currentCountryCode() {
        return this.currentCountry?.iso_code_2?.toLowerCase() ?? '';
    }

    get targetCountryCode() {
        return this.targetCountry?.iso_code_2?.toLowerCase() ?? '';
    }

    get currentCountry() {
        return this.countries.find((country) => this.isCurrentCountry(country)) ?? null;
    }

    get targetCountry() {
        if (!this.geoCountry) {
            return null;
        }

        return this.countries.find((country) => {
            const countryCode = country?.iso_code_2?.toLowerCase();
            return countryCode === this.geoCountry.toLowerCase()
                && Boolean(this.alternateLinksMapping[countryCode]);
        }) ?? null;
    }

    /**
     * Übersetzungstexte holen und HTML-Entities dekodieren
     *
     * @returns {Object}
     */
    getTranslateTexts() {
        const translateTexts = FxxlCore.getConfig()?.server?.translate_text?.shop_suggestion ?? {};
        const countries = Object.fromEntries(
            Object.entries(translateTexts.countries ?? {}).map(([countryCode, texts]) => [
                countryCode,
                Object.fromEntries(
                    Object.entries(texts ?? {}).map(([key, value]) => [
                        key,
                        typeof value === 'string' ? FxxlCore.decodeHtmlEntities(value) : value,
                    ])
                ),
            ])
        );

        return {
            ...translateTexts,
            countries,
        };
    }

    get currentTranslateText() {
        return this.translateTexts?.countries?.[this.currentCountryCode] ?? {};
    }

    get targetTranslateText() {
        return this.translateTexts?.countries?.[this.targetCountryCode] ?? {};
    }

    get currentContentText() {
        return (this.currentTranslateText.content_text ?? '')
            .replace('###TARGET_COUNTRY###', this.targetTranslateText.target_country_text ?? '')
            .replace('###TARGET_DOMAIN###',  this.targetCountry?.urlHost ?? '');
    }

    /**
     * Alternativ Links holen
     *
     * @returns {Object}
     */
    getAlternateLinks() {
        const alternateLinks = {};

        document.querySelectorAll('link[rel="alternate"]').forEach((link) => {
            const hreflang = link.getAttribute('hreflang')?.trim().toLowerCase();
            const href     = link.getAttribute('href');
            if (hreflang && href) {
                alternateLinks[hreflang] = href;
            }
        });

        return alternateLinks;
    }

    /**
     * GeoIP Land holen
     *
     * @returns {Promise<void>}
     */
    async getGeoIpIso2Code() {
        try {
            const response = await fetch(this.getGeoIpIso2CodeUrl);
            const data = await response.json();

            if (data?.status === 'success' && data?.code && typeof data.code === 'string') {
                this.geoCountry = data.code.toUpperCase();
            } else {
                new FxxlError('Failed to fetch country code');
            }
        } catch (error) {
            new FxxlError(error);
        }
    }




    /* ❓️️ CHECKS
     * ----------------------------------------------------------------------------------------- */
    /**
     * Prüfen ob Land zu aktuellem Presence gehört
     *
     * @param country
     * @returns {boolean}
     */
    isCurrentCountry(country) {
        return +country?.c_presence_id === +presence_id;
    }

    /**
     * Prüft, ob der Besucher über eine Google-Domain gekommen ist.
     *
     * @returns {boolean}
     */
    isGoogleReferrer() {
        if (!document.referrer) {
            return false;
        }

        try {
            const hostname = new URL(document.referrer).hostname.toLowerCase();
            return /(^|\.)google\.com$/.test(hostname);
        } catch (error) {
            new FxxlError(error);
            return false;
        }
    }




    /* 📜 SETTER
     * ----------------------------------------------------------------------------------------- */
    setPopupModalSize() {
        if (this.device === 'desktop') {
            this.popupModal.style.maxWidth = '600px';
        }
    }




    /* 👁️‍🗨️ SHOW / HIDE: Popup
     * ----------------------------------------------------------------------------------------- */
    showPopup() {
        this.createSuggestionsPopup();
        document.body.appendChild(this.popupModal);

        setTimeout(() => {
            const instance = M.Modal.init(this.popupModal, {
                onCloseEnd: () => {
                    FxxlShopSuggestion.setStorageSelectedSuggestedCountry(this.currentCountry?.iso_code_2);
                }
            });
            this.setPopupModalSize();
            instance.open();
        }, 100);
    }




    /* 🐉 CONSTRUCTOR
     * ----------------------------------------------------------------------------------------- */
    /**
     * Konstruktor
     *
     * @param device string
     */
    constructor(device = 'desktop') {
        this.device              = device;
        this.geoCountry          = null;
        this.alternateLink       = null;
        this.popupModal          = null;
        this.getGeoIpIso2CodeUrl = '/json.php?service=getGeoIpIso2Code';
        this.fxxlCore            = new FxxlCore();
        this.translateTexts      = this.getTranslateTexts();

        // INFO: Bei Erweiterungen
        // Bei der derzeitigen Umsetzung des Länder-Dialogs wird im Javascript ein Sprachcode als Ländercode interpretiert.
        // Da derzeit die Shops mit genau einer Sprache und genau einem erlaubten Lieferland arbeiten und
        // deren Kürzel im Shop jeweils identisch gepflegt sind, funktioniert das mit den aktuellen Shops (DE, AT, CH).
        // Problematisch könnte es werden, wenn bei einem Shop weitere Sprachen oder Lieferländer hinzukommen.
        // In diesem Fall müsste die Logik noch mal zumindest geprüft und wahrscheinlich erweitert werden.
        this.alternateLinksMapping = {
            'de': 'de-de',
            'at': 'de-at',
            'ch': 'de-ch',
        };

        // Prüfen ob IP Detection Popup deaktiviert ist
        if (+FxxlCore.getConfig()?.server?.presence?.variables?.FXXL_DISABLE_IP_DETECTION_POPUP !== 1) {
            this.init();
        } else {
            FxxlShopSuggestion.setStorageSelectedSuggestedCountry();
        }
    }


    /**
     * Initialisierung
     */
    async init() {
        // GeoIP Land holen
        await this.getGeoIpIso2Code();

        // Prüfen ob Empfehlung angezeigt werden soll
        const currentCountry = this.currentCountry;
        const targetCountry  = this.targetCountry;

        if (currentCountry === null) {
            new FxxlError('Current country not found');
            FxxlShopSuggestion.setStorageSelectedSuggestedCountry();
            return;
        }

        if (targetCountry !== null && !this.isCurrentCountry(targetCountry)) {
            this.doSuggestions(targetCountry);
        } else {
            // Wenn bereits im korrektem Land
            FxxlShopSuggestion.setStorageSelectedSuggestedCountry(currentCountry.iso_code_2);
        }
    }


    /**
     * Vorschlag anzeigen
     *
     * @param suggestionCountry string
     */
    doSuggestions(suggestionCountry) {
        const alternateLinks = this.getAlternateLinks();
        let alternateLink = alternateLinks[this.alternateLinksMapping[suggestionCountry.iso_code_2.toLowerCase()]];

        if (!alternateLink) {
            alternateLink = suggestionCountry.url;
        }

        if (alternateLink) {
            this.alternateLink = this.prepareAlternateLink(alternateLink);
            if (this.alternateLink) {
                this.showPopup();
            }
        }
    }


    /**
     * Vorbereitung der Alternativ Link
     *
     * @param link string
     * @returns {string|null}
     */
    prepareAlternateLink(link) {
        let url;
        try {
            url = new URL(link.trim(), window.location.href);
        } catch (error) {
            new FxxlError(error);
            return null;
        }

        const utmSource      = this.fxxlCore.getParameterByName('utm_source');
        const utmMedium      = this.fxxlCore.getParameterByName('utm_medium');
        const utmCampaign    = this.fxxlCore.getParameterByName('utm_campaign');
        const gadSource      = this.fxxlCore.getParameterByName('gad_source');
        const gadCampaignId  = this.fxxlCore.getParameterByName('gad_campaignid');
        const currentIso2    = this.currentCountry.iso_code_2.toLowerCase();
        const cookieAccepted = +(FxxlCore.getCookie('fxxl_optional_cookies') === true);

        url.searchParams.set('redirected_from_other_language', cookieAccepted);
        url.searchParams.set('utm_content', currentIso2);

        if (utmSource) {
            url.searchParams.set('utm_source', utmSource);
        }
        if (utmMedium) {
            url.searchParams.set('utm_medium', utmMedium);
        }
        if (utmCampaign) {
            url.searchParams.set('utm_campaign', utmCampaign);
        }

        if (gadSource) {
            url.searchParams.set('gad_source', gadSource);

            if (gadCampaignId) {
                url.searchParams.set('gad_campaignid', gadCampaignId);
            }
            if (!utmSource) {
                url.searchParams.set('utm_source', 'google');
            }
            if (!utmMedium) {
                url.searchParams.set('utm_medium', 'cpc');
            }
        } else if (!utmSource && !utmMedium && !utmCampaign && this.isGoogleReferrer()) {
            url.searchParams.set('utm_source', 'google');
            url.searchParams.set('utm_medium', 'organic');
        }

        return url.toString();
    }




    /* 📚️ CREATE: HTML Elements
     * ----------------------------------------------------------------------------------------- */
    createSuggestionsPopup() {
        const currentCountryCode = this.currentCountryCode;
        const targetCountryCode  = this.targetCountryCode;
        const headerText         = this.targetTranslateText.header_text ?? '';
        const contentText        = this.currentContentText;
        const bottomSheetClass   = this.device === 'mobile' ? ' bottom-sheet' : '';

        const template = document.createElement('template');
        template.innerHTML = `
            <div class="fxxl-modal modal${bottomSheetClass}">
                <div class="fxxl-modal__header modal-header">
                    <div class="fxxl-modal__header-text"></div>
                    <a class="fxxl-modal__header-close modal-close">
                        <svg width="24" height="24">
                            <use href="#sprite_icon_close" xlink:href="#sprite_icon_close"></use>
                        </svg>
                    </a>
                </div>
                <div class="fxxl-modal__content modal-content fxxl-shop-suggestion-popup">
                    <div class="fxxl-shop-suggestion-popup__text"></div>
                    <div class="fxxl-shop-suggestion-popup__buttons"></div>
                </div>
            </div>
        `;

        const modal = template.content.firstElementChild;
        const modalHeaderText = modal.querySelector('.fxxl-modal__header-text');
        const modalContentText = modal.querySelector('.fxxl-shop-suggestion-popup__text');
        const modalShopSuggestionButtons = modal.querySelector('.fxxl-shop-suggestion-popup__buttons');

        modalHeaderText.textContent = headerText;
        modalContentText.textContent = contentText;

        // ADD: Buttons
        [currentCountryCode, targetCountryCode].forEach((countryCode) => {
            const button = this.createButton(countryCode);
            if (targetCountryCode === countryCode) {
                button.classList.add('fxxl-shop-suggestion-popup__button--distance');
                button.href = this.alternateLink;
                button.style.order = 1;
                button.addEventListener('click', () => {
                    FxxlShopSuggestion.setStorageSelectedSuggestedCountry(targetCountryCode);
                });
            } else {
                button.classList.add('modal-close');
                button.setAttribute('data-cy', 'shop-suggestion-popup-button-close');
            }
            modalShopSuggestionButtons.appendChild(button);
        });

        this.popupModal = modal;
    }

    /**
     * Erstellt einen Button für das Land
     *
     * @param country string
     * @returns {HTMLAnchorElement}
     */
    createButton(country) {
        const template = document.createElement('template');
        const title = this.translateTexts?.countries?.[country]?.title ?? '';

        template.innerHTML = `
            <a class="fxxl-button fxxl-shop-suggestion-popup__button">
                <img
                    width="24"
                    height="24"
                    class="fxxl-button__icon fxxl-button__icon--left"
                    src="/custom/module/public/fxxl3/assets/img/icons/flags/flag_of_${country}.svg"
                >
                <span class="fxxl-button__text"></span>
            </a>
        `;

        const button = template.content.firstElementChild;
        button.querySelector('.fxxl-button__text').textContent = title;

        return button;
    }

}
